/* global axios */
import ApiClient from './ApiClient';

class KanbanAPI extends ApiClient {
  constructor() {
    super('kanban', { accountScoped: true });
  }

  // Pipelines
  getPipelines() {
    return axios.get(`${this.url}/pipelines`);
  }

  getPipeline(pipelineId) {
    return axios.get(`${this.url}/pipelines/${pipelineId}`);
  }

  createPipeline(data) {
    return axios.post(`${this.url}/pipelines`, data);
  }

  updatePipeline(pipelineId, data) {
    return axios.patch(`${this.url}/pipelines/${pipelineId}`, data);
  }

  deletePipeline(pipelineId) {
    return axios.delete(`${this.url}/pipelines/${pipelineId}`);
  }

  createPipelineWizard(data) {
    return axios.post(`${this.url}/pipelines/wizard`, data);
  }

  // Stages
  getStages(pipelineId) {
    return axios.get(`${this.url}/pipelines/${pipelineId}/stages`);
  }

  createStage(pipelineId, data) {
    return axios.post(`${this.url}/pipelines/${pipelineId}/stages`, data);
  }

  updateStage(pipelineId, stageId, data) {
    return axios.patch(
      `${this.url}/pipelines/${pipelineId}/stages/${stageId}`,
      data
    );
  }

  reorderStage(pipelineId, stageId, position) {
    return axios.patch(
      `${this.url}/pipelines/${pipelineId}/stages/${stageId}/reorder`,
      { position }
    );
  }

  deleteStage(pipelineId, stageId) {
    return axios.delete(
      `${this.url}/pipelines/${pipelineId}/stages/${stageId}`
    );
  }

  // Deals
  getDeals(params = {}) {
    return axios.get(`${this.url}/deals`, { params });
  }

  getDeal(dealId) {
    return axios.get(`${this.url}/deals/${dealId}`);
  }

  createDeal(data) {
    return axios.post(`${this.url}/deals`, data);
  }

  updateDeal(dealId, data) {
    return axios.patch(`${this.url}/deals/${dealId}`, data);
  }

  moveDeal(dealId, stageId) {
    return axios.patch(`${this.url}/deals/${dealId}/move`, {
      stage_id: stageId,
    });
  }

  wonDeal(dealId) {
    return axios.patch(`${this.url}/deals/${dealId}/won`);
  }

  lostDeal(dealId, lostReason = '') {
    return axios.patch(`${this.url}/deals/${dealId}/lost`, {
      lost_reason: lostReason,
    });
  }

  deleteDeal(dealId) {
    return axios.delete(`${this.url}/deals/${dealId}`);
  }

  // Products
  getProducts(params = {}) {
    return axios.get(`${this.url}/products`, { params });
  }

  createProduct(data) {
    return axios.post(`${this.url}/products`, data);
  }

  updateProduct(productId, data) {
    return axios.patch(`${this.url}/products/${productId}`, data);
  }

  deleteProduct(productId) {
    return axios.delete(`${this.url}/products/${productId}`);
  }

  // Documents
  getDocuments(dealId) {
    return axios.get(`${this.url}/deals/${dealId}/documents`);
  }

  createDocument(dealId, data) {
    return axios.post(`${this.url}/deals/${dealId}/documents`, data);
  }

  updateDocument(dealId, documentId, data) {
    return axios.patch(
      `${this.url}/deals/${dealId}/documents/${documentId}`,
      data
    );
  }

  deleteDocument(dealId, documentId) {
    return axios.delete(`${this.url}/deals/${dealId}/documents/${documentId}`);
  }

  // Stage Messages (Transition Messages)
  getStageMessages(pipelineId) {
    return axios.get(`${this.url}/pipelines/${pipelineId}/stage_messages`);
  }

  createStageMessage(pipelineId, data) {
    const formData = this.buildStageMessageFormData(data);
    return axios.post(
      `${this.url}/pipelines/${pipelineId}/stage_messages`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  }

  updateStageMessage(pipelineId, messageId, data) {
    const formData = this.buildStageMessageFormData(data);
    return axios.patch(
      `${this.url}/pipelines/${pipelineId}/stage_messages/${messageId}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  }

  deleteStageMessage(pipelineId, messageId) {
    return axios.delete(
      `${this.url}/pipelines/${pipelineId}/stage_messages/${messageId}`
    );
  }

  // eslint-disable-next-line class-methods-use-this
  buildStageMessageFormData(data) {
    const fd = new FormData();
    if (data.from_stage_id) fd.append('from_stage_id', data.from_stage_id);
    if (data.to_stage_id) fd.append('to_stage_id', data.to_stage_id);
    if (data.product_id !== undefined)
      fd.append('product_id', data.product_id || '');
    if (data.content !== undefined) fd.append('content', data.content);
    if (data.is_active !== undefined) fd.append('is_active', data.is_active);
    if (data.files) data.files.forEach(f => fd.append('files[]', f));
    if (data.remove_file_ids)
      data.remove_file_ids.forEach(id => fd.append('remove_file_ids[]', id));
    return fd;
  }
}

export default new KanbanAPI();
